import { useEffect, useState } from 'react';
import { DownloadCloud, Loader2, Search } from 'lucide-react';
import { coreCall } from '../../lib/services/gatewayService';
import { ficConfigService } from '../../lib/services/ficConfigService';
import { getUserData } from '../../lib/utils';

/** Match cliente FIC — stessa shape dell'endpoint core POST /api/fattureincloud/search-clients. */
export interface FicMatch {
  id: number;
  name: string | null;
  vat_number: string | null;
  tax_code: string | null;
  email: string | null;
  certified_email: string | null;
  ei_code: string | null;
  address_street: string | null;
  address_postal_code: string | null;
  address_city: string | null;
  address_province: string | null;
}

interface FicClientLookupProps {
  /** Testo di ricerca iniziale (ragione sociale del cliente). */
  defaultQuery: string;
  /** Chiamato al clic su un match: il chiamante compila i campi vuoti. */
  onPick: (match: FicMatch) => void;
}

const inputCls =
  'w-full px-3 py-2 text-sm border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors';

export function FicClientLookup({ defaultQuery, onPick }: FicClientLookupProps) {
  const [connected, setConnected] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(defaultQuery);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [matches, setMatches] = useState<FicMatch[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Il pulsante compare solo se l'org ha Fatture in Cloud collegato.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { organizationId } = await getUserData();
        if (!organizationId) return;
        const cfg = await ficConfigService.getConfig(organizationId);
        if (!cancelled) setConnected(Boolean(cfg?.is_connected));
      } catch {
        if (!cancelled) setConnected(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!connected) return null;

  const search = async () => {
    const term = query.trim();
    if (term.length < 2 || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: callError } = await coreCall<{ clients: FicMatch[] }>(
        'fattureincloud/search-clients',
        { body: { query: term } },
      );
      if (callError || !data) {
        const msg = callError?.message || '';
        setError(
          msg.includes('scaduto') || msg.includes('FicTokenExpired')
            ? 'Token Fatture in Cloud scaduto: riprova tra qualche minuto o riconnetti l’integrazione.'
            : 'Ricerca su Fatture in Cloud non riuscita. Puoi compilare i campi a mano.',
        );
        setMatches([]);
      } else {
        setMatches(data.clients);
      }
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-[12px] text-[#2383E2] hover:text-[#1a6cba] transition-colors"
      >
        <DownloadCloud className="w-3.5 h-3.5" />
        Cerca su Fatture in Cloud
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-[#2383E2]/25 bg-[#2383E2]/[0.04] p-3">
      <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF]">
        Cerca il cliente su Fatture in Cloud
      </label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void search();
            }
          }}
          placeholder="Nome o email del cliente"
          className={`${inputCls} flex-1`}
        />
        <button
          type="button"
          onClick={() => void search()}
          disabled={loading || query.trim().length < 2}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white rounded-md bg-[#2383E2] hover:bg-[#1a6cba] disabled:opacity-50 transition-colors flex-shrink-0"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Cerca
        </button>
      </div>

      {error && <p className="text-[11px] text-[#EB5757]">{error}</p>}

      {searched && !error && matches.length === 0 && (
        <p className="text-[11px] text-[#6B6762] dark:text-[#9B9A97]">
          Nessun cliente trovato su Fatture in Cloud per "{query.trim()}".
        </p>
      )}

      {matches.length > 0 && (
        <ul className="divide-y divide-[rgba(55,53,47,0.09)] dark:divide-[#454B4E] rounded-md border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] bg-white dark:bg-[#2F3437] max-h-48 overflow-y-auto">
          {matches.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => onPick(m)}
                className="w-full text-left px-3 py-2 hover:bg-[#2383E2]/[0.06] transition-colors"
              >
                <span className="block text-[13px] font-medium text-[#37352F] dark:text-white">
                  {m.name || 'Senza nome'}
                </span>
                <span className="block text-[11px] text-[#6B6762] dark:text-[#9B9A97]">
                  {[m.vat_number, m.email, m.address_city].filter(Boolean).join(' · ') || 'Nessun dettaglio'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
